import { Fragment } from 'react';
import { G } from '@react-pdf/renderer';
import Icons from 'globalConstants/Icons/Report';
import { isEmpty, keys } from 'lodash';

/**
* @function LoadingDocks
* @description Renders loading dock icons in a PDF document based on container or area configuration.
* Calculates dock positions along walls and handles ghost docks.
* 
* @param {Object} props
* @param {boolean} props.inContainer - Whether rendering container-level docks
* @param {string} props.areaId - ID of specific area to render
* @param {Object} props.bbox - Bounding box dimensions
* @param {number} props.scale - Scale factor for dimensions
* @param {Object} props.content - Room and dock configuration data
* @param {Object} props.content.loadingDocks - Loading dock flags by side
* @param {Array<string>} props.content.ghostDocks - IDs of hidden docks
* @param {Object} props.content.areas - Areas data by ID
* @param {Object} props.constants - Configuration constants
* @param {number} props.constants.container.baySize - Size of loading bay
* 
* @description
* Calculates dock positions by:
* 1. Computing horizontal dock spacing and quantity
* 2. Computing vertical dock spacing and quantity
* 3. Filtering active docks based on container/area visibility
* 4. Excluding ghost docks from rendering
* 5. Applying appropriate transforms for dock orientation
* 
* @note
* - Uses @react-pdf/renderer for PDF output
* - Docks are rendered using SVG icons
* - Positions calculated to distribute docks evenly
* - Supports both container and area-level docks
* - Handles ghost docks for selective visibility
*/
const LoadingDocks = (props) => {
  const { inContainer, areaId, bbox, scale, content: { loadingDocks, ghostDocks, areas }, constants } = props;
  const area = areaId ? areas?.[areaId] : {};
  let docks = [];
  const bay = constants.container?.baySize * scale;
  if (!isEmpty(loadingDocks)) {
    // Horizontal calculation
    let qty = (bbox.width - 2) / bay;
    const hqty = (qty % 1 === 0 ? qty - 1 : parseInt(qty)) - (loadingDocks.left_docks + loadingDocks.right_docks);
    const hstart = (bbox.width - 2) / 2 - (hqty + loadingDocks.right_docks - loadingDocks.left_docks) * bay / 2;
    const horizontal = Array.from({ length: hqty }, (_, index) => hstart + index * bay);
    // Vertical calculation
    qty = (inContainer ? bbox.height - 4 : area?.dimensions?.width * scale) / bay;
    const vqty = (qty % 1 === 0 ? qty - 1 : parseInt(qty)) - (loadingDocks.top_docks + loadingDocks.bottom_docks);
    const vstart = (inContainer ? bbox.height - 4 : area?.dimensions?.width * scale) / 2 - (vqty + loadingDocks.bottom_docks - loadingDocks.top_docks) * bay / 2;
    const vertical = Array.from({ length: vqty }, (_, index) => vstart + index * bay);

    // Merge docks
    const sides = inContainer ? loadingDocks : keys(loadingDocks).reduce((acc, key) => ({ ...acc, [key]: area?.id.includes(key) }), {});
    docks = vertical.flatMap((pos, index) => {
      return [
        ...sides.left_docks && !ghostDocks.includes(`left-${index + 1}`) ? [{ x: 1, y: pos + 1, rotate: 90 }] : [],
        ...sides.right_docks && !ghostDocks.includes(`right-${index + 1}`) ? [{ x: (inContainer ? bbox.width - 2 : area?.dimensions?.length * scale), y: pos + 1, rotate: -90 }] : []
      ]
    }).concat(horizontal.flatMap((pos, index) => {
      return [
        ...sides.top_docks && !ghostDocks.includes(`top-${index + 1}`) ? [{ x: pos + 1, y: 1, rotate: 180 }] : [],
        ...sides.bottom_docks && !ghostDocks.includes(`bottom-${index + 1}`) ? [{ x: pos + 1, y: (inContainer ? bbox.height - 4 : area?.dimensions?.width * scale), rotate: 0 }] : []
      ]
    }));
  };
  return (
    <Fragment>
      {docks.map((dock, index) =>
        <G key={`dock-${index}`} transform={`translate(${dock.x} ${dock.y}) rotate(${dock.rotate}) translate(-${[-90, 180].includes(dock.rotate) * bay} 0) scale(${scale / 10})`} >
          <Icons.LoadingDock />
        </G>
      )}
    </Fragment>
  )
};

export default LoadingDocks;